import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { profileData } from "@entities/profile/api/userApi";
import { AUTH_PROFILE_QUERY_KEY } from "@entities/profile/model/profileQuery";
import LoadingContainer from "@shared/ui/LoadingContainer/LoadingContainer";
import { hasValidToken } from "@shared/lib/auth/tokenUtils";
import { AuthContext } from "./AuthContext";
import styles from "./AuthProvider.module.scss";

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const hasToken = hasValidToken();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: AUTH_PROFILE_QUERY_KEY,
    queryFn: profileData,
    staleTime: 10 * 60 * 1000, // matches the backend's 600s cache on /profile/
    gcTime: 15 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: false,
    enabled: hasToken,
  });

  if (isLoading && !data) return <LoadingContainer height="100vh" />;

  if (isError) {
    const status = error?.response?.status;
    if (status === 403) {
      return <Navigate to="/activate" replace />;
    }
    if (status === 401) {
      return <Navigate to="/auth/login" replace />;
    }
    return (
      <div className={styles.errorPage}>
        <p>{t("errors.loadUserFailed")}</p>
        <button onClick={() => refetch()}>{t("errors.retry")}</button>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        userData: data?.data ?? null,
        isLoading,
        isAuthenticated: !!data?.data,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
